import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import isVerifier from './isVerifier';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import * as styles from './VerificationVerifiersFields.module.scss';
import AvatarStackEditable from '../components/AvatarStackEditable';
import Button from '../components/Button';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import EntitySelect from '../search/entitySearch/EntitySelect';
import { useAllUsersMap } from '../user/useAllUsers';
import UserRow from '../user/UserRow';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';

const POST_FRAGMENT = gql(`
  fragment VerificationVerifiersFields_post on Post {
    id
    owner {
      id
      ...UserRow_user
    }
    ...usePostPolicyCheck_post
    ...isVerifier_post
  }
`);

interface VerificationVerifiersFieldsProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  primaryVerifierId: string;
  secondaryVerifierIds: string[];
  setPrimaryVerifier: (id: string) => void;
  setSecondaryVerifierIds: (ids: string[]) => void;
}

const VerificationVerifiersFields = ({
  post,
  primaryVerifierId,
  secondaryVerifierIds,
  setPrimaryVerifier,
  setSecondaryVerifierIds,
}: VerificationVerifiersFieldsProps) => {
  const postPolicyCheck = usePostPolicyCheck();
  const [isEditingPrimary, setIsEditingPrimary] = useState(false);
  const [isAddingSecondary, setIsAddingSecondary] = useState(false);
  const allUsers = useAllUsersMap();

  const canBeVerifier = (user: { id: string }) =>
    postPolicyCheck(PostPolicyAction.EditPost, post, user) ||
    isVerifier(post, user);

  return (
    <div className={styles.verifiers}>
      <div className={styles.verifiersItem}>
        <div className={styles.verifiersTitle}>Primary verifier</div>
        <UserRow user={allUsers[primaryVerifierId] ?? post.owner} />
        <div className={styles.editPrimary}>
          {isEditingPrimary ? (
            <ClickOutsideHandler
              className={styles.userInput}
              onClickOutside={() => setIsEditingPrimary(false)}
            >
              <EntitySelect
                autoFocus
                isClearable={false}
                containerClassName={styles.primarySelect}
                onChange={(result) => {
                  if (result?.type !== 'user') return;
                  const { user } = result;
                  setPrimaryVerifier(user.id);
                  setSecondaryVerifierIds(
                    secondaryVerifierIds.includes(user.id)
                      ? // Swap places with the current primary verifier
                        secondaryVerifierIds.map((id) =>
                          id === user.id ? primaryVerifierId : id,
                        )
                      : // Or append to the end
                        [...secondaryVerifierIds, primaryVerifierId],
                  );
                  setIsEditingPrimary(false);
                }}
                value={{ type: 'user', id: primaryVerifierId }}
                config={{
                  user: {
                    filter: (user) =>
                      user.id !== primaryVerifierId && canBeVerifier(user),
                  },
                }}
                menuPlacement="bottom"
                menuIsOpen
              />
            </ClickOutsideHandler>
          ) : (
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditingPrimary(true);
              }}
            >
              Change
            </Button>
          )}
        </div>
      </div>
      <div className={styles.verifiersItem}>
        <div className={styles.verifiersTitle}>Additional verifiers</div>
        {isAddingSecondary ? (
          <ClickOutsideHandler
            className={styles.userInput}
            onClickOutside={() => setIsAddingSecondary(false)}
          >
            <EntitySelect
              autoFocus
              isClearable={false}
              containerClassName={styles.secondarySelect}
              onChange={(result) => {
                if (result?.type !== 'user') return;
                const { user } = result;
                setSecondaryVerifierIds([...secondaryVerifierIds, user.id]);
                setIsAddingSecondary(false);
              }}
              config={{
                user: {
                  filter: (user) =>
                    user.id !== primaryVerifierId &&
                    !secondaryVerifierIds.includes(user.id) &&
                    canBeVerifier(user),
                },
              }}
              menuPlacement="bottom"
              menuIsOpen
            />
          </ClickOutsideHandler>
        ) : (
          <div
            className={classNames(styles.avatarsWrapper, {
              [styles.empty]: secondaryVerifierIds.length === 0,
            })}
          >
            <AvatarStackEditable
              users={_.compact(secondaryVerifierIds.map((id) => allUsers[id]))}
              size={32}
              onRemove={(userId) => {
                setSecondaryVerifierIds(
                  secondaryVerifierIds.filter((id) => id !== userId),
                );
              }}
              onAdd={() => setIsAddingSecondary(true)}
            />
            {secondaryVerifierIds.length === 0 && (
              <div className={styles.secondVerifier}>Add 2nd verifier</div>
            )}
            {secondaryVerifierIds.length >= 3 && (
              <div className={styles.warning}>
                The {secondaryVerifierIds.length + 2}th verifier is not
                recommended
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationVerifiersFields;
